import type { CategoriesApi } from "@/v2/api/metadata/categories";
import type { SportsApi } from "@/v2/api/metadata/sports";
import type { DialogConfig } from "@/v2/framework/dialog/types";
import { buildAddSupplementalNamesPayload } from "@/v2/kits/supplemental-names";
import { categoryGamesStatusOptions, categoryStatusOptions } from "../../shared";

export function createAddDialog(options: {
  categoriesApi: CategoriesApi;
  sportsApi: SportsApi;
}): DialogConfig {
  const { categoriesApi, sportsApi } = options;

  return {
    id: "add-category",
    title: "Add Category",
    trigger: { type: "fab" },
    layout: { type: "popover" },
    structure: {
      type: "stepper",
      steps: [
        {
          id: "main",
          children: [
            {
              type: "group",
              name: "main",
              label: "Main",
              children: [
                {
                  type: "text-field",
                  name: "name",
                  label: "Name",
                  required: true,
                  placeholder: "Enter a name",
                },
                {
                  type: "select-field",
                  name: "sportId",
                  label: "Sport",
                  required: true,
                  placeholder: "Select sport",
                  searchable: true,
                  options: async () => {
                    const sports = await sportsApi.getFilters({ page: 1, size: -1 });
                    return sports.map((sport) => ({
                      value: sport.id,
                      title: String(sport.name ?? ""),
                    }));
                  },
                  defaultValue: null,
                },
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  placeholder: "Select status",
                  options: categoryStatusOptions,
                },
                {
                  type: "text-field",
                  name: "info",
                  label: "Info",
                  placeholder: "Enter info",
                },
                {
                  type: "text-field",
                  name: "code",
                  label: "Code",
                  placeholder: "Enter code",
                },
                {
                  type: "component",
                  component: () =>
                    import("@/v2/kits/supplemental-names/components/SupplementalNamesFields.vue"),
                  props: { mode: "add", entity: "CATEGORY" },
                },
              ],
            },
            {
              type: "group",
              name: "prematchGame",
              label: "Pre-match",
              children: [
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  placeholder: "Select pre-match status",
                  required: true,
                  options: categoryGamesStatusOptions,
                },
                {
                  type: "text-field",
                  name: "position",
                  label: "Position",
                  placeholder: "Enter position",
                },
                {
                  type: "structured-array-field",
                  name: "displayIds",
                  label: "Display IDs",
                  required: true,
                  minItems: 1,
                  maxItems: 50,
                  children: [
                    {
                      type: "text-field",
                      name: "prefix",
                      label: "",
                      placeholder: "Prefix",
                      flex: 1,
                    },
                    {
                      type: "text-field",
                      name: "rangeFrom",
                      label: "",
                      placeholder: "From",
                      inputType: "number",
                      flex: 1,
                    },
                    {
                      type: "text-field",
                      name: "rangeTo",
                      label: "",
                      placeholder: "To",
                      inputType: "number",
                      flex: 1,
                    },
                  ],
                },
                {
                  type: "select-field",
                  name: "defaultEventSelection",
                  label: "Default Event Selection",
                  placeholder: "Select default event selection for pre-match game",
                  required: true,
                  options: [
                    { value: 1, title: "Selected" },
                    { value: 2, title: "Not Selected" },
                  ],
                },
              ],
            },
            {
              type: "group",
              name: "liveGame",
              label: "Live",
              children: [
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  options: categoryGamesStatusOptions,
                  placeholder: "Select live status",
                },
                {
                  type: "text-field",
                  name: "position",
                  label: "Position",
                  placeholder: "Enter position",
                },
                {
                  type: "structured-array-field",
                  name: "displayIds",
                  label: "Display IDs",
                  required: true,
                  minItems: 1,
                  maxItems: 50,
                  children: [
                    {
                      type: "text-field",
                      name: "prefix",
                      label: "",
                      placeholder: "Prefix",
                      flex: 1,
                    },
                    {
                      type: "text-field",
                      name: "rangeFrom",
                      label: "",
                      placeholder: "From",
                      inputType: "number",
                      flex: 1,
                    },
                    {
                      type: "text-field",
                      name: "rangeTo",
                      label: "",
                      placeholder: "To",
                      inputType: "number",
                      flex: 1,
                    },
                  ],
                },
                {
                  type: "select-field",
                  name: "defaultEventSelection",
                  label: "Default Event Selection",
                  placeholder: "Select default event selection for live game",
                  required: true,
                  options: [
                    { value: 1, title: "Selected" },
                    { value: 2, title: "Not Selected" },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    api: {
      submit: (data) => {
        const model: Record<string, unknown> = {};

        model.sportId = data.main.sportId;
        model.name = data.main.name;
        model.status = data.main.status;
        model.info = data.main.info;
        model.code = data.main.code;
        model.supplementalNames = buildAddSupplementalNamesPayload(data, "CATEGORY");
        model.prematchGame = {
          status: data.prematchGame.status,
          position: data.prematchGame.position,
          displayIds: data.prematchGame.displayIds,
          defaultEventSelection: data.prematchGame.defaultEventSelection,
        };
        model.liveGame = {
          status: data.liveGame.status,
          position: data.liveGame.position,
          displayIds: data.liveGame.displayIds,
          defaultEventSelection: data.liveGame.defaultEventSelection,
        };

        return categoriesApi.create(model);
      },
    },
  };
}
